import './senior-dcm.professional-caregiver.screen.scss';
import {
  Redirect,
  Route,
  Switch,
  useParams,
  useRouteMatch,
  useHistory,
  useLocation,
  matchPath,
} from 'react-router-dom';
import {SeniorSidebarContextProvider} from '../../../../../../contexts/seniors-sidebar/provider';
import {SeniorDcmSidePanel} from '../../../../../../components/senior-dcm-side-panel/senior-dcm-side-panel';
import {CarePlanSheet} from '../../../../../../components/care-plan/_root/care-plan-sheet';
import {CareAndActivity} from '../../../../../../components/care-and-activity/care-and-activity';
import SeniorPersonalDataProfessionalCaregiverOfficeScreen from './senior-personal-data.professional-caregiver.screen';
import {EntityActivitiesRepository} from '../../../../../../repositories/entity-activities';
import {Environment, ScreenPaths} from '../../../../../../enums';
import {useAppContext} from '../../../../../../contexts/app/context';
import {useAuth} from '../../../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import PermissionsRequiredRoute from '../../../../../../components/permissions-required-route';
import {
  BADL_SECTOR_ID,
  CLINICAL_SUPPORT_SECTOR_ID,
  IADL_SECTOR_ID,
  PERSONAL_DEVELOPMENT_SECTOR_ID,
} from '../../../../../../utils/constants';
import {useEffect, useMemo, useState} from 'react';
import SeniorDashboardProfessionalCaregiverOfficeScreen from './senior-dashboard.professional-caregiver.screen';
import {IndividualPlanScreen} from '../individual-plan/individual-plan';
import {ActivitiesSector} from '../../../../../../backend/interfaces/activities-sector';
import AccessRequiredRoute from '../../../../../../components/access-required-route';
import {SeniorCareServicesProfessionalCaregiverScreen} from './care-services/senior-care-services.professional-caregiver.screen';
import {ProfessionalRemindersPage} from './professional-reminders-page';

// !!! NOTE: This assumes sectors are the same for all entities, as specified in the utils/constants/hardcoded.ts file.
const SECTOR_ID_TO_PERMISSION_TYPE_MAPPING: Record<string, RolePermissionType> = {
  [BADL_SECTOR_ID]: RolePermissionType.Seniors_BADL,
  [IADL_SECTOR_ID]: RolePermissionType.Seniors_IADL,
  [CLINICAL_SUPPORT_SECTOR_ID]: RolePermissionType.Seniors_ClinicalSupport,
  [PERSONAL_DEVELOPMENT_SECTOR_ID]: RolePermissionType.Seniors_PersonalDevelopment,
};

export default function SeniorDcmProfessionalCaregiverOfficeScreen() {
  let {path, url} = useRouteMatch();
  const appContext = useAppContext();
  const canAccessDevelopmentFeatures =
    appContext.state.configuration.environment === Environment.Development;

  const location = useLocation();

  let history = useHistory();

  let {entityId, id}: {entityId: string; id: string} = useParams();

  const [activitiesSectors, setActivitiesSectors] = useState<ActivitiesSector[]>([]);
  useEffect(() => {
    EntityActivitiesRepository.instance.getAllSectors(entityId).then((data) => {
      setActivitiesSectors(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    state: {
      session: {
        selectedAffiliation: {contractsOverview: {includesDigitalCareManagement = false} = {}} = {},
      } = {},
    } = {},
    hasReadPermission,
  } = useAuth();

  const showSeniorSidePanel = useMemo(
    () =>
      hasReadPermission(RolePermissionType.Seniors_Dashboard) ||
      hasReadPermission(RolePermissionType.Seniors_BADL) ||
      hasReadPermission(RolePermissionType.Seniors_IADL) ||
      hasReadPermission(RolePermissionType.Seniors_ClinicalSupport) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalDevelopment) ||
      (canAccessDevelopmentFeatures &&
        hasReadPermission(RolePermissionType.Seniors_PersonalData_Reminders)) ||
      hasReadPermission(RolePermissionType.Seniors_CareAndActivities) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalData_Profile) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalData_Caregivers) ||
      hasReadPermission(RolePermissionType.Seniors_PersonalData_Health) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Applications) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Devices) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Playlists) ||
      hasReadPermission(RolePermissionType.Seniors_DigitalInclusion_Cards) ||
      hasReadPermission(RolePermissionType.Seniors_Dashboard_Activity),
    [hasReadPermission, canAccessDevelopmentFeatures]
  );

  const onSeniorChange = (newSeniorId: string) => {
    // Split current location
    let currentLocation = location.pathname.split('/');

    // Find index of id after switch `/seniors`
    const seniorIdIndex = currentLocation.indexOf('seniors') + 1;

    // Replace the senior id
    currentLocation[seniorIdIndex] = newSeniorId;

    // Join them again
    const newPath = currentLocation.join('/');

    // Push new history entry
    history.push(newPath);
  };

  let carePlanRequiredPermission: RolePermissionType | undefined;
  const matchedPath = matchPath(location.pathname, {
    path: `${path}${ScreenPaths.Senior_CarePlan}`,
    exact: true,
  });
  if (matchedPath) {
    carePlanRequiredPermission =
      // @ts-ignore
      SECTOR_ID_TO_PERMISSION_TYPE_MAPPING[matchedPath.params.activitiesSectorId];
  }

  // Build
  return (
    <SeniorSidebarContextProvider>
      <div className="senior-dcm">
        {showSeniorSidePanel && (
          <SeniorDcmSidePanel
            activitiesSectors={activitiesSectors}
            onSeniorChange={onSeniorChange}
          />
        )}
        <div className="main-panel-container">
          <Switch>
            {/* <PermissionsRequiredRoute
              exact
              path={`${path}${ScreenPaths.Senior_Dashboard}`}
              requiredPermissions={[
                {
                  type: RolePermissionType.Seniors_Dashboard,
                  canRead: true, // users must be able to at least read the dashboard to access the page
                },
              ]}
            >
              <SeniorStatisticsProfessionalCaregiverOfficeScreen />
            </PermissionsRequiredRoute>
            <PermissionsRequiredRoute
              exact
              path={`${path}${ScreenPaths.Senior_Dashboard_Health}`}
              requiredPermissions={[
                {
                  type: RolePermissionType.Seniors_Dashboard,
                  canRead: true, // users must be able to at least read the dashboard to access the page
                },
              ]}
            >
              <SeniorStatisticsHealthProfessionalCaregiverOfficeScreen />
            </PermissionsRequiredRoute> */}
            <PermissionsRequiredRoute
              path={`${path}${ScreenPaths.Senior_Dashboard}`}
              key={`senior_${id}_dashboard`}
              requiredPermissions={[
                {
                  type: RolePermissionType.Seniors_Dashboard,
                  canRead: true, // users must be able to at least read the dashboard to access the page
                },
              ]}
            >
              <SeniorDashboardProfessionalCaregiverOfficeScreen />
            </PermissionsRequiredRoute>
            {carePlanRequiredPermission && (
              <PermissionsRequiredRoute
                exact
                path={`${path}${ScreenPaths.Senior_CarePlan}`}
                requiredPermissions={[
                  {
                    type: carePlanRequiredPermission,
                    canRead: true, // users must be able to at least read the given to access the page
                  },
                ]}
              >
                <CarePlanSheet
                  hasDCMaccess={includesDigitalCareManagement}
                  seniorUserId={id}
                  activitiesSectors={activitiesSectors}
                />
              </PermissionsRequiredRoute>
            )}
            <PermissionsRequiredRoute
              exact
              path={`${path}${ScreenPaths.Senior_CareAndActivity}`}
              requiredPermissions={[
                {
                  type: RolePermissionType.Seniors_CareAndActivities,
                  canRead: true, // users must be able to at least read the dashboard to access the page
                },
              ]}
            >
              <CareAndActivity />
            </PermissionsRequiredRoute>
            {canAccessDevelopmentFeatures && (
              <PermissionsRequiredRoute
                exact
                path={`${path}${ScreenPaths.Senior_Reminders}`}
                requiredPermissions={[
                  {
                    type: RolePermissionType.Seniors_PersonalData_Reminders,
                    canRead: true,
                  },
                ]}
              >
                <ProfessionalRemindersPage />
              </PermissionsRequiredRoute>
            )}
            <AccessRequiredRoute
              access="care_management"
              exact
              path={`${path}${ScreenPaths.Senior_CareServices}`}
              requiredPermissions={[
                {
                  type: RolePermissionType.Seniors_CareServices,
                  canRead: true, // users must be able to at least read the dashboard to access the page
                },
              ]}
            >
              <SeniorCareServicesProfessionalCaregiverScreen />
            </AccessRequiredRoute>
            <PermissionsRequiredRoute
              exact
              path={`${path}${ScreenPaths.Senior_IndividualPlan}`}
              requiredPermissions={[
                {
                  type: RolePermissionType.Seniors_IndividualPlan,
                  canRead: true, // users must be able to at least read the dashboard to access the page
                },
              ]}
            >
              <IndividualPlanScreen />
            </PermissionsRequiredRoute>
            <Route
              path={`${path}${ScreenPaths.Senior_PersonalData}`}
              key={`senior_${id}_personal_data`}
            >
              <SeniorPersonalDataProfessionalCaregiverOfficeScreen />
            </Route>
            <Route
              path={`${path}${ScreenPaths.Senior_DigitalInclusion}`}
              key={`senior_${id}_digital_inclusion`}
            >
              <SeniorPersonalDataProfessionalCaregiverOfficeScreen
                defaultRoute={ScreenPaths.Senior_Applications}
              />
            </Route>
            {/** Default route */}
            <Route path={path}>
              <Redirect to={`${url}${ScreenPaths.Senior_PersonalData}`} />
            </Route>
          </Switch>
        </div>
      </div>
    </SeniorSidebarContextProvider>
  );
}
